import { Icons } from '@assets/svg'
import { colors, theme } from '@config/theme'
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { Box, IconButton, useMediaQuery } from '@mui/material'
import { GoogleMap, LoadScript } from '@react-google-maps/api'
import { MapPopup } from '@stories/Components/MapPopup/MapPopup'
import { useRef, useState } from 'react'
import ReactDOMServer from 'react-dom/server'

const libraries: ('places' | 'drawing' | 'geometry' | 'localContext' | 'visualization' | 'marker')[] = [
  'places',
  'marker',
]

const MapViewComponent = ({ camps }: { camps: any[] }) => {
  const isLargeScreen: boolean = useMediaQuery(theme.device['md'])

  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([])
  const hasLoadedRef = useRef(false)
  const markers = markersRef.current
  const selectedMarkerIdRef = useRef<number | null>(null)
  const [selectedPoint, setSelectedPoint] = useState<any | null>(null)

  // State for map center and zoom (for test attributes)
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [mapZoom, setMapZoom] = useState<number | null>(null)

  // Function to handle user location
  const handleLocateMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          if (mapRef.current) {
            mapRef.current.setCenter(coords)
            mapRef.current.setZoom(14)
          }
        },
        (error) => {
          alert(`Error getting location: ${error.message}`)
        }
      )
    } else {
      alert('Geolocation is not supported by your browser.')
    }
  }

  const handleMapLoad = async (map: google.maps.Map) => {
    mapRef.current = map

    if (!window.google?.maps?.marker) {
      console.error('AdvancedMarkerElement is not available.')
      return
    }

    const { AdvancedMarkerElement } = window.google.maps.marker
    const bounds = new window.google.maps.LatLngBounds()

    for (const point of camps) {
      if (!point.dirty && point.lat && point.lng) {
        const position = { lat: point.lat, lng: point.lng }

        const getMarkerSVG = (isSelected: boolean) =>
          ReactDOMServer.renderToStaticMarkup(
            <Icons.MapMarkerIcon
              colors={isSelected ? ['#A44890', '#A44890', '#fff'] : ['#FFE6FA', '#D88EC8', '#101828']}
            />
          )
        const isSelected = selectedMarkerIdRef.current === point.id
        const svgString = getMarkerSVG(isSelected)
        const parser = new DOMParser()
        const svgElement = parser.parseFromString(svgString, 'image/svg+xml').documentElement

        const marker = new AdvancedMarkerElement({
          position,
          map,
          content: svgElement,
          title: point.title,
        })

        svgElement.setAttribute('data-testid', `map-marker-address-${point.id}`)

        // Add click listener to the marker
        marker.addListener('gmp-click', () => {
          // Revert the previously selected marker's color
          if (selectedMarkerIdRef.current !== null) {
            const previouslySelectedMarkerIndex = camps.findIndex(
              (markerPoint) => markerPoint.id === selectedMarkerIdRef.current
            )
            if (previouslySelectedMarkerIndex !== -1) {
              const updatedSvgString = getMarkerSVG(false)
              const updatedSvg = new DOMParser().parseFromString(updatedSvgString, 'image/svg+xml').documentElement
              updatedSvg.setAttribute('data-testid', `map-marker-address-${camps[previouslySelectedMarkerIndex].id}`)
              markers[previouslySelectedMarkerIndex].content = updatedSvg
            }
          }

          // Update the clicked marker's SVG
          const clickedMarkerIndex = camps.findIndex((markerPoint) => markerPoint.id === point.id)
          if (clickedMarkerIndex !== -1) {
            const updatedSvgString = getMarkerSVG(true)
            const updatedSvg = new DOMParser().parseFromString(updatedSvgString, 'image/svg+xml').documentElement
            updatedSvg.setAttribute('data-testid', `map-marker-address-${point.id}`)
            markers[clickedMarkerIndex].content = updatedSvg
          }

          // Update the selected marker reference and state
          selectedMarkerIdRef.current = point.id
          setSelectedPoint(point)
        })

        markers.push(marker)
        bounds.extend(position)
      } else {
        console.warn('Invalid point:', point)
      }
    }
    if (!bounds.isEmpty() && !hasLoadedRef.current) {
      map.fitBounds(bounds)

      google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom()
        if (currentZoom !== undefined) {
          map.setZoom(currentZoom + 1)
        }
      })

      hasLoadedRef.current = true
    }

    // Create the clusterer with AdvancedMarkerElement markers
    new MarkerClusterer({
      map,
      markers,
      algorithm: new SuperClusterAlgorithm({
        minPoints: 5,
      }),
      renderer: {
        render: ({ count, position }) => {
          const div = document.createElement('div')

          div.style.color = '#000'
          div.style.fontSize = '14px'
          div.style.fontFamily = 'Inter, sans-serif'
          div.style.fontWeight = 'bold'
          div.style.borderRadius = '50%'
          div.style.padding = '12px'
          div.style.display = 'flex'
          div.style.alignItems = 'center'
          div.style.justifyContent = 'center'
          div.style.border = '2px solid #D88EC8'
          div.style.boxShadow = '0 0 8px rgba(0,0,0,0.3)'
          div.style.width = '40px'
          div.style.height = '40px'
          div.style.backgroundColor = '#FFE6FA'
          div.textContent = count.toString()

          div.setAttribute('data-testid', `marker-cluster`)

          return new window.google.maps.marker.AdvancedMarkerElement({
            position,
            content: div,
          })
        },
      },
    })
  }

  // Update map center/zoom on map events
  const handleMapIdle = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter()
      const zoom = mapRef.current.getZoom()
      if (center && typeof zoom === 'number') {
        setMapCenter({ lat: center.lat(), lng: center.lng() })
        setMapZoom(zoom)
      }
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: isLargeScreen ? '700px' : '600px',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
      data-testid="map-view-component"
      data-map-center-lat={mapCenter ? mapCenter.lat.toFixed(2) : undefined}
      data-map-center-lng={mapCenter ? mapCenter.lng.toFixed(2) : undefined}
      data-map-zoom={mapZoom !== null ? mapZoom : undefined}
    >
      {/* Locate Me Button */}
      <IconButton
        onClick={handleLocateMe}
        sx={{
          position: 'absolute',
          bottom: 116,
          right: 10,
          zIndex: 10,
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          background: '#fff',
          border: '1px solid #A9B6FC',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          '&:hover': { background: '#f5f5f5' },
        }}
        aria-label="Locate Me"
        data-testid="user-location-button"
      >
        <Icons.NavigationArrowIcon color={colors.brandGray700} size={22} />
      </IconButton>
      <LoadScript googleMapsApiKey="AIzaSyBKdf6YcE17EJKdVK4nsA6MPrrMpRFw520" libraries={libraries as any}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          onLoad={handleMapLoad}
          onIdle={handleMapIdle}
          {...(camps.length === 0 && { center: { lat: 41, lng: -101 }, zoom: 4 })}
          options={{
            mapId: 'YOUR_MAP_ID',
            // gestureHandling: 'greedy',
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: true,
            mapTypeControl: false,
            clickableIcons: false,
            cameraControl: false,
            keyboardShortcuts: false,
            // styles: [
            //   {
            //     featureType: 'poi',
            //     stylers: [{ visibility: 'off' }],
            //   },
            //   // {
            //   //   featureType: 'transit',
            //   //   stylers: [{ visibility: 'off' }],
            //   // },
            //   // {
            //   //   featureType: 'road',
            //   //   elementType: 'labels',
            //   //   stylers: [{ visibility: 'off' }],
            //   // },
            // ],
          }}
        >
          {selectedPoint && (
            <MapPopup
              map={mapRef.current}
              selectedPoint={selectedPoint}
              onClose={() => {
                setSelectedPoint(null)
                const previouslySelectedMarkerIndex = camps.findIndex(
                  (markerPoint) => markerPoint.id === selectedMarkerIdRef.current
                )
                selectedMarkerIdRef.current = null

                // Revert only the previously selected marker's color
                if (previouslySelectedMarkerIndex !== -1) {
                  const updatedSvgString = ReactDOMServer.renderToStaticMarkup(
                    <Icons.MapMarkerIcon colors={['#FFE6FA', '#D88EC8', '#101828']} />
                  )

                  const updatedSvg = new DOMParser().parseFromString(updatedSvgString, 'image/svg+xml').documentElement
                  updatedSvg.setAttribute('data-testid', `map-marker-${camps[previouslySelectedMarkerIndex].id}`)
                  markers[previouslySelectedMarkerIndex].content = updatedSvg
                }
              }}
            />
          )}
        </GoogleMap>
      </LoadScript>
    </Box>
  )
}

export default MapViewComponent
